import React, { memo, useCallback, useEffect } from 'react';
import { useKeyPress, useStore, useStoreApi } from 'reactflow';
import useFlowsStore from '../../store/storeFlows';
import { Tooltip } from '@mui/material';
import { addEdgeTombstones } from '../../util/edgeTombstones';

interface EdgeDeleteButtonProps {
  id: string;
  labelX: number;
  labelY: number;
}

function EdgeDeleteButton({ id, labelX, labelY }: EdgeDeleteButtonProps) {
  const onEdgeDelete = useFlowsStore((state) => state.onEdgeDelete);
  const storeApi = useStoreApi();

  const zoom = useStore((s) => s.transform[2]);
  const isDeletePressed = useKeyPress('Delete');

  const handleDelete = useCallback(
    (event?: React.MouseEvent) => {
      if (event) event.stopPropagation();
      const { edges, setEdges } = storeApi.getState();
      const edge = edges.find(e => e.id === id);
      if (edge) {
        // Tombstone first so no concurrent sync can resurrect this edge, then
        // update the Zustand store synchronously — in the same call frame as the
        // click, before React renders. The edge disappears from the canvas
        // immediately regardless of network state or React batching.
        addEdgeTombstones([id]);
        setEdges(edges.filter(e => e.id !== id));
        onEdgeDelete([edge], id);
      }
    },
    [id, storeApi, onEdgeDelete],
  );

  useEffect(() => {
    if (isDeletePressed) handleDelete();
  }, [isDeletePressed]);

  return (
    <Tooltip title="Delete Edge" placement="top" arrow>
      <button
        type="button"
        onClick={handleDelete}
        data-edge-id={id}
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px) scale(${Math.min(
            1 / zoom,
            3,
          )})`,
          pointerEvents: 'all',
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: 'red',
          color: 'white',
          border: '2px solid white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: 'bold',
          lineHeight: 1,
          padding: 0,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          zIndex: 1000,
        }}
      >
        ✕
      </button>
    </Tooltip>
  );
}

export default memo(EdgeDeleteButton);
